import { IPlayer, GameState, GameMode, IBoardTile, PipValue, ITile, BoardEnd, IRoundResult, AIDifficulty, GameEvents } from './types';
import { DominoRules } from './DominoRules';
import { TurnManager } from './TurnManager';
import { AIEngine } from '../ai/AIEngine';
import { EventBus } from '../utils/EventBus';

/**
 * Central controller for orchestrating the game.
 */
export class GameManager {
  private state: GameState;
  private board: IBoardTile[];
  private players: IPlayer[];
  private turnManager: TurnManager;
  private aiEngine: AIEngine;
  private remainingDeck: ITile[];

  constructor() {
    this.state = GameState.Idle;
    this.board = [];
    this.players = [];
    this.turnManager = new TurnManager([]);
    this.aiEngine = new AIEngine(AIDifficulty.Medium);
    this.remainingDeck = [];
  }

  /**
   * Starts a new game.
   */
  public startNewGame(mode: GameMode, difficulty: AIDifficulty, players: IPlayer[]): void {
    this.state = GameState.Dealing;
    this.players = players;
    this.turnManager = new TurnManager(this.players);
    this.aiEngine.setDifficulty(difficulty);
    this.board = [];

    const fullDeck = DominoRules.shuffleTiles(DominoRules.createTileSet());
    const { hands, remaining } = DominoRules.dealTiles(fullDeck, this.players.length);
    this.remainingDeck = remaining;

    for (let i = 0; i < this.players.length; i++) {
      this.players[i].hand = hands[i];
    }

    let startingPlayerIndex = 0;
    let highestDoubleVal = -1;

    for (let i = 0; i < this.players.length; i++) {
      const highestDouble = DominoRules.findHighestDouble(this.players[i].hand);
      if (highestDouble && highestDouble.a > highestDoubleVal) {
        highestDoubleVal = highestDouble.a;
        startingPlayerIndex = i;
      }
    }

    this.turnManager.setStartingPlayer(startingPlayerIndex);
    this.state = GameState.Playing;
    
    EventBus.emit(GameEvents.GameStart);
    EventBus.emit(GameEvents.TurnStart, { playerId: this.turnManager.currentPlayer.id });
    this.checkAITurn();
  }

  /**
   * Gets the board ends.
   */
  public getBoardEnds(): { left: PipValue | null; right: PipValue | null } {
    if (this.board.length === 0) {
      return { left: null, right: null };
    }
    return {
      left: this.board[0].leftValue,
      right: this.board[this.board.length - 1].rightValue
    };
  }

  /**
   * Plays a tile.
   */
  public playTile(playerId: string, tile: ITile, preferredEnd?: BoardEnd): boolean {
    if (this.state !== GameState.Playing) return false;
    
    const player = this.players.find(p => p.id === playerId);
    if (!player || this.turnManager.currentPlayer.id !== playerId) return false;

    const { left, right } = this.getBoardEnds();
    if (!DominoRules.canPlayTile(tile, left, right)) {
      EventBus.emit(GameEvents.TileInvalid, { playerId, tile });
      return false;
    }

    const possibleEnds = DominoRules.getPlayableEnd(tile, left, right);
    const end = (preferredEnd && possibleEnds.includes(preferredEnd)) ? preferredEnd : possibleEnds[0];

    const endValue = end === 'left' ? left : right;
    const boardTile = DominoRules.orientTile(tile, end, endValue);

    if (end === 'left') {
      this.board.unshift(boardTile);
    } else {
      this.board.push(boardTile);
    }

    player.hand = player.hand.filter(t => t.id !== tile.id);
    EventBus.emit(GameEvents.TilePlayed, { playerId, tile, end, boardTile });

    if (player.hand.length === 0) {
      this.endRound(player.id, false);
      return true;
    }

    if (this.isGameBlocked()) {
      this.endRound(null, true);
      return true;
    }

    this.turnManager.nextTurn();
    this.checkAITurn();
    return true;
  }

  /**
   * Passes the turn.
   */
  public passTurn(playerId: string): boolean {
    if (this.state !== GameState.Playing) return false;
    
    const player = this.players.find(p => p.id === playerId);
    if (!player || this.turnManager.currentPlayer.id !== playerId) return false;

    const { left, right } = this.getBoardEnds();
    if (DominoRules.hasPlayableTile(player.hand, left, right)) {
      return false; // Cannot pass if playable tiles exist
    }

    EventBus.emit(GameEvents.PlayerPassed, { playerId });

    if (this.isGameBlocked()) {
      this.endRound(null, true);
      return true;
    }

    this.turnManager.nextTurn();
    this.checkAITurn();
    return true;
  }

  /**
   * Checks if game is blocked.
   */
  public isGameBlocked(): boolean {
    const { left, right } = this.getBoardEnds();
    return this.players.every(p => !DominoRules.hasPlayableTile(p.hand, left, right));
  }

  /**
   * Ends the round.
   */
  public endRound(winnerId: string | null, isBlocked: boolean): void {
    this.state = GameState.RoundOver;
    let finalWinnerId = winnerId;
    let isDraw = false;

    const scores = this.players.map(p => ({
      playerId: p.id,
      name: p.name,
      remainingPips: DominoRules.calculateRemainingPips(p.hand)
    }));

    if (isBlocked && !finalWinnerId) {
      scores.sort((a, b) => a.remainingPips - b.remainingPips);
      if (scores[0].remainingPips === scores[1].remainingPips) {
        isDraw = true;
      } else {
        finalWinnerId = scores[0].playerId;
      }
    }

    const winner = this.players.find(p => p.id === finalWinnerId);

    const result: IRoundResult = {
      winnerId: finalWinnerId,
      winnerName: winner ? winner.name : '',
      isDraw,
      isBlocked,
      scores,
      coinsEarned: finalWinnerId && !isDraw ? 50 : 10,
      xpEarned: finalWinnerId && !isDraw ? 100 : 25
    };

    EventBus.emit(GameEvents.RoundEnd, result);
  }

  /**
   * Gets playable tiles for a player.
   */
  public getPlayableTilesForPlayer(playerId: string): ITile[] {
    const player = this.players.find(p => p.id === playerId);
    if (!player) return [];
    const { left, right } = this.getBoardEnds();
    return DominoRules.getPlayableTiles(player.hand, left, right);
  }

  /**
   * Gets current state.
   */
  public getState(): GameState {
    return this.state;
  }

  /**
   * Checks if AI turn and plays.
   */
  private checkAITurn(): void {
    const current = this.turnManager.currentPlayer;
    if (current.type === 'ai' && this.state === GameState.Playing) {
      setTimeout(() => {
        const { left, right } = this.getBoardEnds();
        if (DominoRules.hasPlayableTile(current.hand, left, right)) {
          const move = this.aiEngine.selectPlay(current.hand, left, right, this.board);
          if (move) {
            this.playTile(current.id, move.tile, move.end);
          }
        } else {
          this.passTurn(current.id);
        }
      }, 1000);
    }
  }
}
